import { useState, type Dispatch, type SetStateAction } from 'react';
import { Avatar } from '../Avatar';
import { Skeleton } from '../Skeleton';
import { EmptyState } from '../EmptyState';
import { useToast } from '../Toast';
import { Button } from '../ui';
import { markFeedbackRead, type FeedbackRow } from '../../lib/admin-queries';

const CATEGORY_LABELS: Record<string, string> = {
  tech: 'Tech',
  feature: 'Feature Request',
  feedback: 'Feedback',
  idea: 'Idea',
  billing: 'Billing',
  other: 'Other',
};

export function FeedbackPanel({
  feedback,
  setFeedback,
  loading,
}: {
  feedback: FeedbackRow[];
  setFeedback: Dispatch<SetStateAction<FeedbackRow[]>>;
  loading: boolean;
}) {
  const toast = useToast();
  const [feedbackTab, setFeedbackTab] = useState<'new' | 'all' | 'read'>('new');
  const [feedbackCategory, setFeedbackCategory] = useState<string>('all');
  const [feedbackBusy, setFeedbackBusy] = useState<string | null>(null);

  return (
    <>
      <div className="admin__panel-head">
        <div className="admin__head-titles">
          <h2>Contact inbox</h2>
          <span className="admin__count">
            {feedback.filter((f) => !f.is_read).length} new / {feedback.length} total
          </span>
        </div>
        <label className="admin__feedback-filter">
          <span className="visually-hidden">Filter by category</span>
          <select
            className="admin__select"
            value={feedbackCategory}
            onChange={(e) => setFeedbackCategory(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="all">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="admin__tabs" role="tablist" aria-label="Contact filter">
        {(['new', 'all', 'read'] as const).map((tab) => {
          const count =
            tab === 'new'  ? feedback.filter((f) => !f.is_read).length :
            tab === 'read' ? feedback.filter((f) =>  f.is_read).length :
                             feedback.length;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={feedbackTab === tab}
              className={`admin__tab ${feedbackTab === tab ? 'admin__tab--active' : ''}`}
              onClick={() => setFeedbackTab(tab)}
            >
              {tab === 'new' ? 'New' : tab === 'all' ? 'All' : 'Read'}
              <span className="admin__tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <ul className="admin__feedback" aria-busy="true" aria-label="Loading messages">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="admin__feedback-item admin__feedback-item--skel">
              <Skeleton variant="text" width="30%" height={12} />
              <div style={{ height: 6 }} />
              <Skeleton variant="text" width="90%" />
              <Skeleton variant="text" width="75%" />
            </li>
          ))}
        </ul>
      ) : (() => {
        const filtered = feedback.filter((f) => {
          const matchesTab =
            feedbackTab === 'new'  ? !f.is_read :
            feedbackTab === 'read' ?  f.is_read :
                                      true;
          const matchesCategory =
            feedbackCategory === 'all' || f.category === feedbackCategory;
          return matchesTab && matchesCategory;
        });
        if (filtered.length === 0) {
          return (
            <EmptyState
              tone="compact"
              align="start"
              eyebrow={feedbackTab === 'new' ? 'Inbox zero' : 'Quiet so far'}
              headline={
                feedbackTab === 'new' ? 'Nothing new to read.' :
                feedbackTab === 'read' ? 'Nothing in the archive yet.' :
                                         'No messages yet.'
              }
              body={
                feedbackTab === 'read'
                  ? 'Items you mark as read land here.'
                  : 'Testers hit the floating Contact pill on any page, it lands here.'
              }
            />
          );
        }
        return (
          <ul className="admin__feedback">
            {filtered.map((f) => (
              <li
                key={f.id}
                className={`admin__feedback-item ${f.is_read ? 'admin__feedback-item--read' : 'admin__feedback-item--unread'}`}
              >
                <div className="admin__feedback-head">
                  <Avatar
                    name={f.author?.display_name || 'Anonymous'}
                    src={f.author?.avatar_url}
                    size="sm"
                  />
                  <div className="admin__feedback-meta">
                    <span className="admin__feedback-name">
                      {f.author?.display_name || 'Anonymous'}
                      {f.author?.handle && <span className="admin__feedback-handle"> @{f.author.handle}</span>}
                    </span>
                    <span className="admin__feedback-time">
                      {new Date(f.created_at).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                      {f.page_path && <> · <code>{f.page_path}</code></>}
                    </span>
                  </div>
                  {f.category && (
                    <span className="admin__feedback-category">
                      {CATEGORY_LABELS[f.category] ?? f.category}
                    </span>
                  )}
                  {!f.is_read && <span className="admin__feedback-dot" aria-label="Unread" />}
                </div>
                <p className="admin__feedback-message">{f.message}</p>
                <div className="admin__feedback-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={feedbackBusy === f.id}
                    onClick={async () => {
                      setFeedbackBusy(f.id);
                      try {
                        await markFeedbackRead(f.id, !f.is_read);
                        setFeedback((prev) =>
                          prev.map((x) => (x.id === f.id ? { ...x, is_read: !f.is_read } : x)),
                        );
                        toast.success(f.is_read ? 'Marked unread.' : 'Marked read.');
                      } catch (err: any) {
                        toast.error(err?.message ?? 'Update failed.');
                      } finally {
                        setFeedbackBusy(null);
                      }
                    }}
                  >
                    {feedbackBusy === f.id
                      ? '…'
                      : f.is_read
                        ? 'Mark unread'
                        : 'Mark read'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        );
      })()}
    </>
  );
}
